import { updateAppAlert } from '../../../common/modules/appAlert/actions'
import { getErrorMessages } from '../../../common/utils/error'
import { errorLogger } from '../../../common/utils/errorLogger'
import { AppDispatch, RootState } from '../../../store'
import { AxiosError, AxiosResponse } from 'axios'
import {
  createWAFRAssessmentSuccess,
  deleteWorkloadSuccess,
  RequestFailureMessage,
  updateWorkloadSuccess,
} from '../../../common/utils/messagesContants'
import { isEmpty } from 'common/utils/vanilla'
import {
  startLoading,
  stopLoading,
} from '../../../common/modules/loading/actions'
import { LoadingTypes } from '../../../common/modules/loading/reducer'

import { camelize, snakeize } from 'casing'
import {
  CreateWAFRAssessmentResponse,
  CreateWAFRAssessmentRequest,
  WAFRAssessmentFormDetails,
  WAFRQuestion,
  ScopeItem,
} from './reducer'
import {
  createWAFRAssessment,
  downloadLensReport,
  fetchWAFRWorkloadById,
  updateWAFRAssessment,
  getWAFRAssessmentQuestions,
  deleteWorkload,
  getStatus,
  downloadCISReport,
} from '../../api/cloudfaas/wafr'
import { emptyWorkloadAssessment } from '../../utils/wafr/constants'
import { awsRegionsList } from '../../../common/utils/Cloudfaas/wafr/AssessformDataSelect'

export enum WAFRAssessmentForm {
  SET_WAFR_CREATE_ASSESSMENT_FORM = 'SET_WAFR_CREATE_ASSESSMENT_FORM',
  SET_WAFR_QUESTIONS = 'SET_WAFR_QUESTIONS',
  SET_WAFR_QUESTION_ID = 'SET_WAFR_QUESTION_ID',
  SET_WAFR_QUESTION_REF_NO = 'SET_WAFR_QUESTION_REF_NO',
  SET_DELAY = 'SET_DELAY',
  REMOVE_DELAY = 'REMOVE_DELAY',
  SET_STATUS = 'SET_STATUS',
}

const PILLAR_IDS = [
  'operationalExcellence',
  'security',
  'reliability',
  'performance',
  'costOptimization',
  'sustainability',
]

export const createNewWAFRAssessment =
  (
    partnerId: string,
    assessmentData: CreateWAFRAssessmentRequest,
    callBackWAFRWorkloadDetails?: (workloadId: string) => void
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const snakeCasePayload = snakeize(assessmentData)

    await dispatch(startLoading(LoadingTypes.CREATE_WAFR_ASSESSMENT))
    try {
      const response = await createWAFRAssessment(partnerId, snakeCasePayload)

      if (response?.data) {
        const assessmentsData = camelize(response.data)
        const workloadId = assessmentsData?.workloadId
        await dispatch(
          invokeWorkloadCallback({
            workloadId,
            callBackWAFRWorkloadDetails,
          })
        )
      }

      dispatch(
        updateAppAlert({
          message: createWAFRAssessmentSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.CREATE_WAFR_ASSESSMENT))
    }
  }

export const updateWAFRAssessmentData =
  (
    partnerId: string,
    workloadId: string,
    assessmentData: CreateWAFRAssessmentRequest,
    callBackWAFRWorkloadDetails?: (workloadId: string) => void
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.CREATE_WAFR_ASSESSMENT))
    try {
      const snakeCasePayload = snakeize(assessmentData)

      const response = await updateWAFRAssessment(
        partnerId,
        workloadId,
        snakeCasePayload
      )

      if (!isEmpty(response)) {
        const assessmentsData = camelize(response)
        const workloadId = assessmentsData?.workloadId
        await dispatch(
          invokeWorkloadCallback({
            workloadId,
            callBackWAFRWorkloadDetails,
          })
        )
      }

      dispatch(
        updateAppAlert({
          message: updateWorkloadSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.CREATE_WAFR_ASSESSMENT))
    }
  }

export const getWAFRWorkloadById =
  (partnerId: string, workloadId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const response = await fetchWAFRWorkloadById(partnerId, workloadId)
      if (response?.data) {
        const workloadData = camelize(response?.data)
        const mappedWorkloadAssessment = {
          workloadId: workloadData.workloadId,
          workloadName: workloadData.workloadName,
          reviewOwner: workloadData.reviewOwner,
          description: workloadData.description,
          environment: workloadData.environment,
          hostedRegion: workloadData.hostedRegion,
          architecturalDesign: workloadData.architecturalDesign,
          industryType: workloadData.industryType,
          industry: workloadData.industry,
          workloadArn: workloadData?.workloadArn,
          scope: (workloadData?.scope as ScopeItem[])?.map(item => ({
            accountId: item.accountId || '',
            awsRegions: item.awsRegions?.map(regionCode => {
              const match = awsRegionsList.find(r => r.region === regionCode)
              return match || { region: '', regionName: '' }
            }) || [{ region: '', regionName: '' }],
          })),
        }
        await dispatch(setWAFRAssessmentFormData(mappedWorkloadAssessment))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

interface invokeWorkloadCallbackProps {
  workloadId?: string
  callBackWAFRWorkloadDetails?: (workloadId: string) => void
}

export const invokeWorkloadCallback =
  ({ workloadId, callBackWAFRWorkloadDetails }: invokeWorkloadCallbackProps) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (workloadId && callBackWAFRWorkloadDetails) {
      callBackWAFRWorkloadDetails(workloadId)
    }
  }

export const getWAFRQuestions =
  (partnerId: string, workloadId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(startLoading(LoadingTypes.GET_WAFR_QUESTION_PILLAR))
      const allQuestions: WAFRQuestion[] = []

      await Promise.all(
        PILLAR_IDS.map(async pillarId => {
          try {
            const response = await getWAFRAssessmentQuestions(
              partnerId,
              workloadId,
              'wellarchitected',
              pillarId
            )
            if (response?.data) {
              const assessmentsData = camelize(response?.data)
              const questions = assessmentsData.answerSummaries.map(
                (summary: any) => ({
                  questionId: summary.questionId,
                  pillarId: summary.pillarId,
                  questionTitle: summary.questionTitle,
                  status: summary.choiceAnswerSummaries?.at(-1)?.status,
                  questionReferenceNumber: summary.questionReferenceNumber,
                })
              )
              allQuestions.push(...questions)
            }
          } catch (error) {
            console.error(
              `Error fetching questions for pillar ${pillarId}:`,
              error
            )
          }
        })
      )

      const sortedQuestions = allQuestions.sort((a, b) => {
        const indexA = PILLAR_IDS.indexOf(a.pillarId)
        const indexB = PILLAR_IDS.indexOf(b.pillarId)
        return indexA - indexB
      })

      dispatch(setWAFRQuestions(sortedQuestions))
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GET_WAFR_QUESTION_PILLAR))
    }
  }

export const initializeWAFRAssessmentForm =
  ({ partnerId, workloadId }: { partnerId: string; workloadId: string }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(startLoading(LoadingTypes.SET_ASSESSMENT_FORM))
      if (isEmpty(workloadId)) {
        const initialFormData = getInitialWAFRAssessmentForm()
        await dispatch(setWAFRAssessmentFormData(initialFormData))
      } else {
        dispatch(getWAFRWorkloadById(partnerId, workloadId))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.SET_ASSESSMENT_FORM))
    }
  }

export const setWAFRQuestionId = (questionId: string) => ({
  type: WAFRAssessmentForm.SET_WAFR_QUESTION_ID,
  payload: questionId,
})

export const setWAFRQuestionRefNo = (questionReferenceNumber: string) => ({
  type: WAFRAssessmentForm.SET_WAFR_QUESTION_REF_NO,
  payload: questionReferenceNumber,
})

export const getInitialWAFRAssessmentForm = () => {
  return emptyWorkloadAssessment
}

export const setWAFRAssessmentFormData = (
  WAFRAssessmentFormData: WAFRAssessmentFormDetails
) => ({
  type: WAFRAssessmentForm.SET_WAFR_CREATE_ASSESSMENT_FORM,
  payload: { createAssessmentInfo: WAFRAssessmentFormData },
})

export const downloadWorkloadLenseReport =
  (partnerId: string, workloadId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.DOWNLOAD_WAFR_REPORT))
    try {
      const reportResponse = await downloadLensReport(partnerId, workloadId)
      const response = camelize(reportResponse)
      if (response?.data?.lensReviewReport?.fileUrl) {
        // Create an anchor element for the download
        const name = response?.data?.name || 'workload_report'
        const fileName = `${name}.xlsx`
        const link = document.createElement('a')
        link.href = response?.data?.lensReviewReport?.fileUrl
        link.setAttribute('download', fileName)

        // Append the link to the DOM, trigger click, and remove it after download
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        throw new Error('Download URL not found')
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.DOWNLOAD_WAFR_REPORT))
    }
  }

export const setWAFRQuestions = (questions: WAFRQuestion[]) => ({
  type: WAFRAssessmentForm.SET_WAFR_QUESTIONS,
  payload: questions,
})

export const deleteWorkloadById =
  (partnerId: string, workloadId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.DELETE_WORKLOAD))
    try {
      await deleteWorkload(partnerId, workloadId)
      dispatch(
        updateAppAlert({
          message: deleteWorkloadSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.DELETE_WORKLOAD))
    }
  }

export const fetchStatus =
  (partnerId: string, workloadId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GET_WAFR_STATUS))
    try {
      const response = await getStatus(partnerId, workloadId)
      const summary = response.data.summary
      if (summary) {
        dispatch(setStatus(summary))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GET_WAFR_STATUS))
    }
  }

export const setDelay = () => ({
  type: WAFRAssessmentForm.SET_DELAY,
})

export const removeDelay = () => ({
  type: WAFRAssessmentForm.REMOVE_DELAY,
})

export const setStatus = (status: []) => ({
  type: WAFRAssessmentForm.SET_STATUS,
  payload: status,
})

export const downloadWorkloadCISReport =
  (partnerId: string, workloadId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.DOWNLOAD_CIS_REPORT))
    try {
      const reportResponse = await downloadCISReport(partnerId, workloadId)

      const response = camelize(reportResponse)

      if (response?.data?.metadata?.reportUrl) {
        // Create an anchor element for the download
        const name = response?.data?.assessmentId || 'CIS_report'
        const fileName = `${name}.xlsx`
        const link = document.createElement('a')
        link.href = response?.data?.metadata?.reportUrl
        link.setAttribute('download', fileName)

        // Append the link to the DOM, trigger click, and remove it after download
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        dispatch(
          updateAppAlert({
            message: 'Something went wrong',
            messageType: 'ERROR',
            autoClose: true,
          })
        )
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.DOWNLOAD_CIS_REPORT))
    }
  }
